import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';
import { matchPath, pickQueryParams, sendApplicationError, sendException, createCustomError } from '@/lib/sentryClient';

const RESERVE_API_URL = import.meta.env.VITE_RESERVE_API_URL;
const reserveAPIPathList = ['/api/reservation/getreservationlist/:cognitoid/:today'];

export interface Result<T> {
  result: boolean;
  data?: T;
  error?: Error;
}

export const reserveAPIClient = axios.create({
  baseURL: `${RESERVE_API_URL}/`
});
// インターセプターの設定でエラーを捕捉してSentry側にイベントを送信するようにする
reserveAPIClient.interceptors.request.use((request: InternalAxiosRequestConfig) => {
  const match = matchPath(request.url ?? '', reserveAPIPathList);
  // BreadClumbsに履歴を残す
  Sentry.addBreadcrumb({
    category: `${match?.matchedPath}`,
    message: `${match?.matchedPath} requested`,
    data: {
      path: JSON.stringify(match?.params),
      body: JSON.stringify(request.data),
      auth: request.headers.Authorization ?? 'None'
    }
  });
  return request;
});

reserveAPIClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const match = matchPath(response.config.url ?? '', reserveAPIPathList);
    // BreadClumbsに履歴を残す
    Sentry.addBreadcrumb({
      category: `${match?.matchedPath}`,
      message: `${match?.matchedPath} executed`,
      data: {
        response: JSON.stringify(response.data),
        auth: response.headers.Authorization ?? 'None'
      }
    });
    return response;
  },
  (error: AxiosError) => {
    // pathパラメーター部分の抽出とURLのパスパラメーターの部分を置き換え
    const match = matchPath(error.config?.url ?? '', reserveAPIPathList);
    error.name = `${match?.matchedPath ?? 'unknownURL'} ${error.name}`;
    // クエリパラメーターの抽出
    const queryParams = pickQueryParams(error.config?.url ?? '');
    const extra = {
      HTTP: {
        url: match?.matchedPath ?? error.config?.url ?? 'unknownURL',
        path: JSON.stringify(match?.params),
        query: JSON.stringify(queryParams),
        statusCode: error.response?.status,
        request: JSON.stringify(error.request),
        header: JSON.stringify(error.config?.headers ?? ''),
        auth: error.config?.headers.Authorization ?? 'None'
      }
    };
    // 4xx のエラーをアプリケーションエラーとして拾う
    if (error.response?.status && error.response?.status < 500) {
      sendApplicationError(error, match?.matchedPath ?? error.config?.url ?? 'unknownURL', extra);
      return Promise.reject(createCustomError(error));
    }

    // 5xx はアプリケーションエラーではないので別イシューで管理する
    sendException(error, error.config?.url ?? 'unknownURL', extra);
    return Promise.reject(createCustomError(error));
  }
);

export const get = async (path: string): Promise<Result<any>> => {
  try {
    const response = await reserveAPIClient.get(path);
    return {
      result: true,
      data: response.data
    };
  } catch (error: any) {
    return {
      result: false,
      error: error
    };
  }
};
